import { TRANSLATABLE_LOCALES } from '../i18n/supportedLocales';

/**
 * Locale-aware reader for user-generated content with `_en`/`_zh`/`_ja`
 * translation columns.
 *
 * Returns the matching-locale value when the active locale is en/zh/ja AND
 * the `${base}_${lang}` column has non-empty content; otherwise returns the
 * canonical Spanish value. The fallback to Spanish is intentional — shoppers
 * must never see an empty product card or artist bio just because the seller
 * hasn't translated their copy yet.
 *
 * Example:
 *   const locale = useLocale();                         // 'es-MX' | 'en-US' | 'zh-CN' | 'ja-JP'
 *   const name = tField(product, 'name', locale);
 *
 * Use camelCase or snake_case base names — both are checked, since services
 * normalize columns inconsistently across the codebase (e.g. `description`
 * vs `description_en` are both in the response, while artists may surface
 * `rightsReserved` alongside `rights_reserved_en`).
 */
export function tField(row, base, locale) {
  if (!row || !base) return '';

  const lang = typeof locale === 'string' ? locale.slice(0, 2).toLowerCase() : '';

  if (TRANSLATABLE_LOCALES.includes(lang)) {
    // Try snake_case `${base}_${lang}` first (the column name from Postgres),
    // then camelCase `${baseCamel}${Lang}` (some services rename keys at the
    // boundary), e.g. `name_zh` / `nameZh`.
    const snakeKey = `${base}_${lang}`;
    const camelKey = `${camelCase(base)}${lang[0].toUpperCase()}${lang[1]}`;
    const localeValue = nonEmpty(row[snakeKey]) ?? nonEmpty(row[camelKey]);
    if (localeValue !== undefined && localeValue !== null) return localeValue;
  }

  // Fallback: canonical (Spanish) column. Try both casings for the same reason.
  return (
    nonEmpty(row[base]) ??
    nonEmpty(row[camelCase(base)]) ??
    ''
  );
}

function nonEmpty(value) {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string' && value.trim() === '') return undefined;
  return value;
}

function camelCase(snake) {
  return snake.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

export default tField;
